import type { Json } from '@fluent/types';
import { audioDiagnostics } from '../audio/engine.js';
import { silentSwitchDiagnostics } from '../audio/silentSwitch.js';
import { deviceInfo } from './deviceInfo.js';
import { supabase } from './supabase.js';

/**
 * In-app "report an error" client helper. Inserts one row into the
 * `feedback` table — schema, RLS, and rationale documented in
 * `supabase/migrations/20260428_0005_feedback.sql`.
 *
 * Why a dedicated module instead of inlining in the dialog component:
 *   * Keeps the Supabase client out of the UI. Components stay testable
 *     without route mocking the insert path.
 *   * Single place to evolve the wire format. Every report carries the
 *     device, app version and sound state automatically (`reportContext`).
 *
 * Failure mode: if the insert fails (offline, RLS reject, network),
 * we resolve `{ ok: false, error }`. The dialog UI treats either
 * outcome as "your report was saved — thanks" because:
 *   1. The user already self-reported the issue verbally to themselves.
 *      Surfacing a stack trace adds nothing.
 *   2. We don't want to discourage future reports.
 *   3. Real prod analytics (PostHog, Phase 13) will count failures
 *      separately so we can fix the pipeline.
 */

export type FeedbackReason =
  | 'typo'
  | 'unclear'
  | 'wrong_translation'
  | 'audio_broken'
  | 'other';

export interface ReportErrorArgs {
  /** Lesson slug currently active. */
  lessonId: string;
  /** Exercise slug if the report is exercise-specific. Optional — the
   *  user may flag the lesson as a whole (e.g. "the audio for this whole
   *  skill sounds wrong"). */
  exerciseId?: string;
  reason: FeedbackReason;
  /** Optional free-form note. Capped at 1000 chars by the dialog. */
  message?: string;
  /** Extra context the dialog already knows (locale, last verdict,
   *  app version). Stored verbatim in `feedback.meta`. */
  meta?: Record<string, unknown>;
}

export interface ReportErrorResult {
  ok: boolean;
  error?: string;
}

export async function reportError(args: ReportErrorArgs): Promise<ReportErrorResult> {
  const { data: sess } = await supabase.auth.getSession();
  const userId = sess.session?.user?.id;
  if (!userId) return { ok: false, error: 'not_signed_in' };

  const { error } = await supabase.from('feedback').insert({
    user_id: userId,
    lesson_id: args.lessonId,
    exercise_id: args.exerciseId ?? null,
    reason: args.reason,
    message: args.message?.slice(0, 1000) ?? null,
    // Caller passes a free-shape record; the column is jsonb, so we
    // serialize the value through `Json` after dropping any
    // non-serializable entries (functions, undefined values, etc).
    meta: toJson({ ...reportContext(), ...(args.meta ?? {}) }),
  });
  if (error) return { ok: false, error: error.message };

  // Best-effort email notification to the operator. The feedback row is
  // already saved; the email is courtesy so Tornike sees reports without
  // having to query the table manually. Swallow all failures — the user
  // doesn't care if the email pipeline is having a bad day.
  void notifyOperator(sess.session?.access_token);

  return { ok: true };
}

/**
 * Added to every report (2026-10-08): which phone/browser/app version sent
 * it and what the sound engine was doing — "no sound" reports were
 * undiagnosable without it. Never throws: a report must always save.
 */
export function reportContext(): Record<string, unknown> {
  try {
    return { ...deviceInfo(), audio: { ...audioDiagnostics(), ...silentSwitchDiagnostics() } };
  } catch {
    return {};
  }
}

async function notifyOperator(accessToken: string | undefined): Promise<void> {
  if (!accessToken) return;
  try {
    await fetch('/api/feedback-notify', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    });
  } catch {
    /* best-effort — feedback row is the source of truth */
  }
}

/** Round-trip a free-shape value through JSON.stringify/parse to drop
 *  unsupported types and satisfy the column's `Json` type. Cheap, and
 *  matches what Supabase would do server-side anyway. */
function toJson(value: unknown): Json {
  return JSON.parse(JSON.stringify(value)) as Json;
}
